const API = typeof import.meta !== 'undefined' ? (import.meta.env?.VITE_API_URL || '') : '';

const MEDIA_ID_RE = /\/api\/saas\/v1\/media\/([a-fA-F0-9]{24})(?:\/file)?(?:\?|$)/i;

const IMAGE_EXT_RE = /\.(avif|bmp|gif|jpe?g|png|svg|webp)(?:\?|#|$)/i;
const VIDEO_EXT_RE = /\.(mp4|webm|ogg|mov|m4v)(?:\?|#|$)/i;
const DOC_EXT_RE = /\.(pdf|docx?|xlsx?|pptx?|txt|zip|json)(?:\?|#|$)/i;

export type MediaEmbedKind = 'image' | 'video' | 'document' | 'unknown';

function isLocalMediaHost(hostname: string): boolean {
  const h = hostname.toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h === '::1') return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  return false;
}

/**
 * True for SaaS GridFS media paths (relative or localhost/LAN absolute).
 * Used to gate iframes/embeds — never allow third-party YouTube/Maps URLs.
 * PlatzView/Cut studio links are out of scope (different apps).
 */
export function isSaasInternalMediaSrc(raw: unknown): boolean {
  return parseSaasMediaId(raw) !== null;
}

/** Extract SaaS media ObjectId from relative or absolute local media URLs. */
export function parseSaasMediaId(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!s) return null;
  let pathAndQuery = s;
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      if (!isLocalMediaHost(u.hostname)) return null;
      pathAndQuery = `${u.pathname}${u.search}`;
    } catch {
      return null;
    }
  }
  const m = pathAndQuery.match(MEDIA_ID_RE);
  return m?.[1] || null;
}

/**
 * Prefer a stable relative `/api/saas/v1/media/:id/file` path for persistence.
 * Absolute localhost URLs from the media picker are normalized the same way.
 */
export function toStoredMediaUrl(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const s = raw.trim();
  if (!s) return '';
  const id = parseSaasMediaId(s);
  if (id) {
    let pathAndQuery = s;
    if (/^https?:\/\//i.test(s)) {
      try {
        const u = new URL(s);
        pathAndQuery = `${u.pathname}${u.search}`;
      } catch {
        pathAndQuery = `/api/saas/v1/media/${id}/file`;
      }
    }
    if (pathAndQuery.includes(`/media/${id}/file`)) {
      return pathAndQuery;
    }
    return `/api/saas/v1/media/${id}/file`;
  }
  return s;
}

/**
 * Resolve a stored media/path URL for use in <img>/<video src>.
 * Absolute localhost/LAN SaaS media URLs are normalized via toStoredMediaUrl
 * to relative `/api/...` so the Vite proxy serves them same-origin
 * (avoids cross-origin private-network blocks from :3003 → :4003).
 */
export function resolveMediaSrc(raw: unknown): string {
  const stored = toStoredMediaUrl(raw);
  if (!stored) return '';
  if (stored.startsWith('data:') || stored.startsWith('blob:')) return stored;
  if (stored.startsWith('/')) return `${API}${stored}`;
  if (/^https?:\/\//i.test(stored)) return stored;
  return stored;
}

/**
 * Display URL for media library / picker grids.
 * Prefer relative `/file` (tokenless OK locally); never use bare metadata `url` alone
 * when an id is available — always build a loadable `/file` path.
 */
export function toDisplayMediaUrl(item: {
  _id?: string;
  fileUrl?: string;
  thumbnailUrl?: string;
  url?: string;
  mimeType?: string;
  type?: string;
  originalName?: string;
}): string {
  const candidates = [item.thumbnailUrl, item.fileUrl, item.url];
  for (const c of candidates) {
    if (!c) continue;
    const resolved = resolveMediaSrc(c);
    if (resolved) return resolved;
  }
  if (item._id) {
    return resolveMediaSrc(`/api/saas/v1/media/${item._id}/file`);
  }
  return '';
}

/** True when the media item should render as an <img> thumbnail. */
export function isImageMediaItem(item: { mimeType?: string; type?: string; originalName?: string; fileUrl?: string; url?: string }): boolean {
  if (item.type === 'image') return true;
  if ((item.mimeType || '').toLowerCase().startsWith('image/')) return true;
  return guessMediaKind(item.originalName || item.fileUrl || item.url || '', item.mimeType) === 'image';
}

export function guessMediaKind(raw: unknown, mimeHint?: string): MediaEmbedKind {
  const mime = (mimeHint || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime && (mime.includes('pdf') || mime.startsWith('application/') || mime.startsWith('text/'))) return 'document';

  const s = typeof raw === 'string' ? raw : '';
  if (IMAGE_EXT_RE.test(s)) return 'image';
  if (VIDEO_EXT_RE.test(s)) return 'video';
  if (DOC_EXT_RE.test(s)) return 'document';
  // SaaS media /file without extension — treat as image by default (hero/gallery).
  if (parseSaasMediaId(s)) return 'image';
  return 'unknown';
}

export function mediaFilenameFromUrl(raw: unknown): string {
  if (typeof raw !== 'string' || !raw.trim()) return 'File';
  try {
    const path = /^https?:\/\//i.test(raw) ? new URL(raw).pathname : raw.split('?')[0] || raw;
    const base = path.split('/').filter(Boolean).pop() || 'File';
    if (base === 'file' || /^[a-fA-F0-9]{24}$/.test(base)) return 'Document';
    return decodeURIComponent(base);
  } catch {
    return 'File';
  }
}
